import { Fragment, useState } from "react";
import { Transition } from "@headlessui/react";
import {
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  XMarkIcon,
  ServerIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "@via/utils/helpers";
import { Spinner } from "@via/components/loading/loading";
import type { McpToolCallPendingPayload } from "common";

interface McpToolConfirmationProps {
  pendingCall: McpToolCallPendingPayload;
  onAccept: (callId: string) => void;
  onReject: (callId: string) => void;
  isResponding?: boolean;
}

export const McpToolConfirmation = ({
  pendingCall,
  onAccept,
  onReject,
  isResponding = false,
}: McpToolConfirmationProps) => {
  const [showArgs, setShowArgs] = useState(false);

  const hasArgs =
    pendingCall.arguments && Object.keys(pendingCall.arguments).length > 0;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="animate-in fade-in slide-in-from-bottom-2 duration-300 self-start max-w-[85%] rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-md">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
          <WrenchScrewdriverIcon className="h-4 w-4 text-amber-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-800">
            MCP Tool Call Request
          </p>
          <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
            <ServerIcon className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{pendingCall.serverName}</span>
          </div>
          <div className="mt-2 rounded-md border border-amber-100 bg-white/70 px-3 py-2">
            <p className="text-sm font-medium text-gray-900">
              {pendingCall.toolName}
            </p>
            {pendingCall.toolDescription && (
              <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                {pendingCall.toolDescription}
              </p>
            )}
          </div>

          {hasArgs && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowArgs(!showArgs)}
                aria-expanded={showArgs}
                className="flex items-center gap-1 text-xs text-gray-500 transition-colors duration-200 ease-in-out hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                {showArgs ? (
                  <ChevronUpIcon className="h-3 w-3" />
                ) : (
                  <ChevronDownIcon className="h-3 w-3" />
                )}
                {showArgs ? "Hide" : "Show"} arguments
              </button>
              <Transition
                as={Fragment}
                show={showArgs}
                enter="transition ease-out duration-150"
                enterFrom="transform opacity-0 -translate-y-1"
                enterTo="transform opacity-100 translate-y-0"
                leave="transition ease-in duration-100"
                leaveFrom="transform opacity-100 translate-y-0"
                leaveTo="transform opacity-0 -translate-y-1">
                <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-gray-800 p-2 text-xs text-gray-200 scrollbar-thin">
                  {JSON.stringify(pendingCall.arguments, null, 2)}
                </pre>
              </Transition>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => onAccept(pendingCall.callId)}
              disabled={isResponding}
              className={classNames(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white shadow-sm duration-200 ease-in-out",
                "bg-green-600 hover:bg-green-500",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                isResponding
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer"
              )}>
              {isResponding ? (
                <Spinner loading className="h-3.5 w-3.5" />
              ) : (
                <CheckIcon className="h-3.5 w-3.5" />
              )}
              Accept
            </button>
            <button
              type="button"
              onClick={() => onReject(pendingCall.callId)}
              disabled={isResponding}
              className={classNames(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium shadow-sm duration-200 ease-in-out",
                "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                isResponding
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer"
              )}>
              <XMarkIcon className="h-3.5 w-3.5" />
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
